import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useClassName } from '@travpro/hooks';
import i18n from 'i18n-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { shareUrl } from 'settings';

/**
 * @typedef {object} ShareBoxProps
 * @property {string} image The source url for the image.
 * @property {string} [title=''] The title of the link. Defaults to empty string. Default is `''`
 * @property {string} [url=image] The target url for the link. If no url is provided, the image url is used. Default is `image`
 * @property {subject} [subject=''] The subject of the email. Defaults to empty string. Default is `''`
 * @property {body} [body=''] The body of the email. Defaults to empty string. Default is `''`
 * @property {boolean} [facebook=false] Whether or not to display the Facebook share button. Defaults to false. Default is `false`
 * @property {boolean} [twitter=false] Whether or not to display the Twitter share button. Defaults to false. Default is `false`
 * @property {boolean} [email=false] Whether or not to display the Email share button. Defaults to false. Default is `false`
 * @property {boolean} [download=false] Whether or not to display the Download button. Defaults to false. Default is `false`
 * @property {string} [size=false] The size of the image. Defaults to 'md', including when an invalid size is provided.
 *   Default is `false`
 */

/**
 * A ShareBox component that displays an image and buttons to share a link in various ways.
 *
 * @param {ShareBoxProps} props Props
 */
const ShareBox = ({
  image: imgSrc = '',
  title = '',
  url: urlSrc = null,
  subject: subjectProp = '',
  body: bodyProp = '',
  facebook = false,
  twitter = false,
  email = false,
  download = false,
  size = 'md',
}) => {
  const [ShareBoxLoaded, setShareBoxLoaded] = useState(false);

  // Check to validate if image is a url or a local file.
  const image = imgSrc.includes('http') ? imgSrc : `https://front.travpromobile.com/${appId}/${imgSrc}`;
  // If no url is provided, use the image url.
  const url = urlSrc || image;

  const subject = encodeURIComponent(subjectProp);
  const body = encodeURIComponent(bodyProp);
  const parsedUrl = composeUrl(url, image, title);

  const c = useClassName('sharebox');

  return (
    <div {...c()}>
      <div {...c('content')}>
        <img
          {...c('image', {
            sm: size === 'sm',
            md: size === 'md' ?? (size !== 'sm' && size !== 'lg'),
            lg: size === 'lg',
          })}
          src={image}
          alt={`${subject || 'sharebox-image'}`}
          loading="lazy"
          onLoad={() => setShareBoxLoaded(true)} // Adjusted to set ShareBoxLoaded on image load
        />
      </div>

      {ShareBoxLoaded && (
        <div {...c('buttons')}>
          {facebook && (
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(parsedUrl)}`}
              className="fancybox-share__button fancybox-share__button--fb"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                <path d="m287 456v-299c0-21 6-35 35-35h38v-63c-7-1-29-3-55-3-54 0-91 33-91 94v306m143-254h-205v72h196" />
              </svg>
              <span>{i18n.translate('sharing.facebook')}</span>
            </a>
          )}

          {twitter && (
            <a
              href={`https://twitter.com/share?url=${parsedUrl}&text=${twitterCopy}`}
              className="fancybox-share__button fancybox-share__button--tw"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="m456 133c-14 7-31 11-47 13 17-10 30-27 37-46-15 10-34 16-52 20-61-62-157-7-141 75-68-3-129-35-169-85-22
              37-11 86 26 109-13 0-26-4-37-9 0 39 28 72 65 80-12 3-25 4-37 2 10 33 41 57 77 57-42 30-77 38-122 34 170 111 378-32
              359-208 16-11 30-25 41-42z"
                />
              </svg>
              <span>{i18n.translate('sharing.twitter')}</span>
            </a>
          )}

          {email && (
            <a
              href={`mailto:?subject=${subject}&body=${body}${encodeURIComponent('\n\n')}${parsedUrl}`}
              className="fancybox-share__button fancybox-share__button--pt"
            >
              <FontAwesomeIcon icon={['fas', 'at']} />
              <span>{i18n.translate('sharing.email')}</span>
            </a>
          )}

          {download && (
            <a
              href={`https://stack.travpromobile.com/download.php?url=${image}&name=${title
                .replace(/ /g, '%20')
                .replace('&', '%26')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="fancybox-download__button fancybox-download__button--pt"
            >
              <FontAwesomeIcon icon={['fas', 'download']} />
              <span>{i18n.translate('sharing.download')}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
};

ShareBox.propTypes = {
  image: PropTypes.string.isRequired,
  url: PropTypes.string,
  facebook: PropTypes.bool,
  twitter: PropTypes.bool,
  email: PropTypes.bool,
  download: PropTypes.bool,
  subject: PropTypes.string,
  body: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

const composeUrl = (forwardUrl, imageUrl, title) => {
  let parsedURL = '';

  // Parsing URL for normal images (such as specialist badges)
  if (forwardUrl.match(/[0-9]{3}.*?$/g) === null) {
    const internalURL = forwardUrl.length > 0 ? forwardUrl.match(/[0-9]{3}.*?$/g) : '';

    console.log('tracking where it goes wrong (internal):', internalURL);

    parsedURL = `${shareUrl}?subject=${encodeURI(title)}&image=${encodeURI(
      imageUrl.replace('https://front.travpromobile.com/', 'https://travprocdn.imgix.net/'),
    )}${encodeURIComponent('?')}width=400${encodeURIComponent('&')}file=${internalURL}`;

    console.log('tracking where it goes wrong (parsed):', parsedURL);
  }

  // Parsing URL for dynamic images (metal progress badges)
  // FIXME: Correct the regex to match the correct URL, rather than just not matching the previous check
  if (forwardUrl.includes('https://crm.travpromobile.com/api/view-diploma')) {
    const preProcessedForwardUrl = forwardUrl.replaceAll('&', '%26');
    parsedURL = `${shareUrl}?file=${encodeURIComponent(preProcessedForwardUrl)}&app_id=${appId}`;

    console.log('tracking where it goes wrong (parsed for view-diploma):', parsedURL);
  }

  // Generic replacements
  let urlEncoded = forwardUrl.length > 0 ? parsedURL.replace(/ /g, '%20') : '';
  urlEncoded = urlEncoded.replace('&', '%26');

  // Due to this project using a separate share url, this is not needed. Leaving it in, should it be needed in the future.
  // // If the url is not a share url, make it one
  // if (!['https://share.', 'http://share.'].some((val) => urlEncoded.includes(val))) {
  //   urlEncoded = urlEncoded.replace('https://', 'https://share.');
  //   urlEncoded = urlEncoded.replace('http://', 'http://share.');
  // }

  urlEncoded = urlEncoded.replace('share.travprocdn', 'travprocdn');
  urlEncoded = urlEncoded.replace('https://share.www.canadaspecialist.co.uk', 'https://sharecanada.travpromobile.com');
  urlEncoded = urlEncoded.replace('https://share.www.canadaspecialist.com.au', 'https://sharecanada.travpromobile.com');
  urlEncoded = urlEncoded.replace('https://share.canadaspecialist.com.au', 'https://sharecanada.travpromobile.com');

  urlEncoded = urlEncoded.replace(
    'https://share.www.canadaspecialistprogram.de',
    'https://sharecanada.travpromobile.com',
  );
  urlEncoded = urlEncoded.replace('https://share.canadaspecialistprogram.de', 'https://sharecanada.travpromobile.com');
  urlEncoded = urlEncoded.replace('https://share.www.specialistesducanada.fr', 'https://sharecanada.travpromobile.com');
  urlEncoded = urlEncoded.replace('https://share.specialistesducanada.fr', 'https://sharecanada.travpromobile.com');
  urlEncoded = urlEncoded.replace(
    'https://share.psc-csp.destinationcanada.com',
    'https://sharecanada.travpromobile.com',
  );

  return urlEncoded;
};

export default ShareBox;
